import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Collections as CollectionsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FormatQuote as ExposureMentionIcon,
  Groups as ExposureSharedIcon,
  Label as LabelIcon,
  Public as PublicIcon,
  RemoveCircleOutline as ExposureNoneIcon,
  SentimentSatisfied as SentNeutralIcon,
  SentimentVeryDissatisfied as SentNegativeIcon,
  SentimentVerySatisfied as SentPositiveIcon,
  Share as ShareIcon,
  Star as ExposureDedicatedIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { operationalStatusColors } from '../../../../design-system/tokens';
import { QUALITATIVE_AI_ACCENT } from '../../../../utils/aiAnalysisStatus';

const getSentimentIcon = (sentiment, size = 20) => {
  if (sentiment === 'positive') return <SentPositiveIcon sx={{ fontSize: size, color: '#4caf50' }} />;
  if (sentiment === 'negative') return <SentNegativeIcon sx={{ fontSize: size, color: '#f44336' }} />;
  return <SentNeutralIcon sx={{ fontSize: size, color: '#9e9e9e' }} />;
};

const getExposureIcon = (exposure, size = 20) => {
  if (exposure === 'dedicated') return <ExposureDedicatedIcon sx={{ fontSize: size, color: operationalStatusColors.success }} />;
  if (exposure === 'shared') return <ExposureSharedIcon sx={{ fontSize: size, color: operationalStatusColors.info }} />;
  if (exposure === 'mention') return <ExposureMentionIcon sx={{ fontSize: size, color: 'grey.500' }} />;
  return <ExposureNoneIcon sx={{ fontSize: size, color: 'grey.400' }} />;
};

const ArticleModalActionMenu = ({
  article,
  anchorEl,
  isMobile,
  kpiaAnalyzing,
  onAddToCollection,
  onAnalyzeKpia,
  onClose,
  onDelete,
  onEdit,
  onOpenExposureMenu,
  onOpenPublicLink,
  onOpenSentimentMenu,
  onOpenShare,
  onOpenTagDialog,
  onToggleFavorite,
  onToggleRead,
  qualitativeAnalysisApplied,
  readOnly,
  showKpia
}) => {
  const closeAfter = (callback) => (event) => {
    callback(event);
    onClose();
  };

  const kpiaListItem = showKpia ? (
    <>
      <Divider sx={{ my: 0.5, mx: 2 }} />
      <ListItem disablePadding>
        <ListItemButton onClick={closeAfter(onAnalyzeKpia)} disabled={kpiaAnalyzing}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            {kpiaAnalyzing ? <CircularProgress size={18} /> : <AutoAwesomeIcon sx={{ fontSize: 20, color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'secondary.main' }} />}
          </ListItemIcon>
          <ListItemText
            primary={kpiaAnalyzing ? 'Calculando...' : 'Análise Qualitativa'}
            secondary={qualitativeAnalysisApplied ? 'IA aplicada' : 'Ocupacao e Sentimento Multimodal'}
            primaryTypographyProps={{ color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'secondary.main', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.65rem' }}
          />
        </ListItemButton>
      </ListItem>
    </>
  ) : null;

  const kpiaMenuItem = showKpia ? (
    <>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={closeAfter(onAnalyzeKpia)} disabled={kpiaAnalyzing}>
        <ListItemIcon>
          {kpiaAnalyzing ? <CircularProgress size={18} /> : <AutoAwesomeIcon sx={{ fontSize: 22, color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'secondary.main' }} />}
        </ListItemIcon>
        <ListItemText
          primary={kpiaAnalyzing ? 'Calculando...' : 'Análise Qualitativa'}
          secondary={qualitativeAnalysisApplied ? 'IA aplicada' : 'Ocupacao e Sentimento Multimodal'}
          primaryTypographyProps={{ color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'secondary.main', fontWeight: 600 }}
          secondaryTypographyProps={{ fontSize: '0.65rem' }}
        />
      </MenuItem>
    </>
  ) : null;

  if (readOnly) {
    return (
      <>
        <Drawer
          anchor="bottom"
          open={isMobile && Boolean(anchorEl)}
          onClose={onClose}
          ModalProps={{ keepMounted: false }}
          sx={{ zIndex: 1400 }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: '85vh',
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              pb: 2
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
          </Box>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={closeAfter(onOpenShare)}>
                <ListItemIcon sx={{ minWidth: 40 }}><ShareIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Compartilhar" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={closeAfter(onOpenPublicLink)}>
                <ListItemIcon sx={{ minWidth: 40 }}><PublicIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Link Publico" />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>

        <Menu
          anchorEl={!isMobile ? anchorEl : null}
          open={!isMobile && Boolean(anchorEl)}
          onClose={onClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220, borderRadius: 2 } }}
        >
          <MenuItem onClick={closeAfter(onOpenShare)}>
            <ListItemIcon><ShareIcon sx={{ fontSize: 20 }} /></ListItemIcon>
            <ListItemText>Compartilhar</ListItemText>
          </MenuItem>
          <MenuItem onClick={closeAfter(onOpenPublicLink)}>
            <ListItemIcon><PublicIcon sx={{ fontSize: 20 }} /></ListItemIcon>
            <ListItemText>Link Publico</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  }

  return (
    <>
      <Drawer
        anchor="bottom"
        open={isMobile && Boolean(anchorEl)}
        onClose={onClose}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            pb: 2
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
        </Box>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onOpenShare)}>
              <ListItemIcon sx={{ minWidth: 40 }}><ShareIcon sx={{ fontSize: 20 }} /></ListItemIcon>
              <ListItemText primary="Compartilhar" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onOpenPublicLink)}>
              <ListItemIcon sx={{ minWidth: 40 }}><PublicIcon sx={{ fontSize: 20 }} /></ListItemIcon>
              <ListItemText primary="Link Público" />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 0.5, mx: 2 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onToggleFavorite)}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {article.isFavorite ? <FavoriteIcon sx={{ fontSize: 20, color: 'error.main' }} /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
              </ListItemIcon>
              <ListItemText primary={article.isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onToggleRead)}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {article.isRead ? <VisibilityIcon sx={{ fontSize: 20 }} color="primary" /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
              </ListItemIcon>
              <ListItemText primary={article.isRead ? 'Marcar como Não Lido' : 'Marcar como Lido'} />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 0.5, mx: 2 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onOpenTagDialog)}>
              <ListItemIcon sx={{ minWidth: 40 }}><LabelIcon sx={{ fontSize: 20 }} /></ListItemIcon>
              <ListItemText primary="Adicionar Tag" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={closeAfter(onAddToCollection)}>
              <ListItemIcon sx={{ minWidth: 40 }}><CollectionsIcon sx={{ fontSize: 20 }} /></ListItemIcon>
              <ListItemText primary="Adicionar à Coleção" />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 0.5, mx: 2 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={onOpenSentimentMenu}>
              <ListItemIcon sx={{ minWidth: 40 }}>{getSentimentIcon(article.sentiment)}</ListItemIcon>
              <ListItemText primary="Sentimento" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={onOpenExposureMenu}>
              <ListItemIcon sx={{ minWidth: 40 }}>{getExposureIcon(article.exposure)}</ListItemIcon>
              <ListItemText primary="Exposição da Marca" />
            </ListItemButton>
          </ListItem>
          {kpiaListItem}
          {(onEdit || onDelete) && <Divider sx={{ my: 0.5, mx: 2 }} />}
          {onEdit && (
            <ListItem disablePadding>
              <ListItemButton onClick={closeAfter(() => onEdit(article))}>
                <ListItemIcon sx={{ minWidth: 40 }}><EditIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Editar" />
              </ListItemButton>
            </ListItem>
          )}
          {onDelete && (
            <ListItem disablePadding>
              <ListItemButton onClick={closeAfter(() => onDelete(article._id))} sx={{ color: 'error.main' }}>
                <ListItemIcon sx={{ minWidth: 40 }}><DeleteIcon sx={{ fontSize: 20, color: 'error.main' }} /></ListItemIcon>
                <ListItemText primary="Excluir" primaryTypographyProps={{ color: 'error.main' }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>

      <Menu
        anchorEl={!isMobile ? anchorEl : null}
        open={!isMobile && Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2 } }}
      >
        <MenuItem onClick={closeAfter(onOpenShare)}>
          <ListItemIcon><ShareIcon sx={{ fontSize: 20 }} /></ListItemIcon>
          <ListItemText>Compartilhar</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeAfter(onOpenPublicLink)}>
          <ListItemIcon><PublicIcon sx={{ fontSize: 20 }} /></ListItemIcon>
          <ListItemText>Link Público</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={closeAfter(onToggleFavorite)}>
          <ListItemIcon>{article.isFavorite ? <FavoriteIcon sx={{ color: 'error.main' }} /> : <FavoriteBorderIcon />}</ListItemIcon>
          <ListItemText>{article.isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeAfter(onToggleRead)}>
          <ListItemIcon>{article.isRead ? <VisibilityIcon color="primary" /> : <VisibilityOffIcon />}</ListItemIcon>
          <ListItemText>{article.isRead ? 'Marcar como Não Lido' : 'Marcar como Lido'}</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={closeAfter(onOpenTagDialog)}>
          <ListItemIcon><LabelIcon /></ListItemIcon>
          <ListItemText>Adicionar Tag</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeAfter(onAddToCollection)}>
          <ListItemIcon><CollectionsIcon /></ListItemIcon>
          <ListItemText>Adicionar à Coleção</ListItemText>
        </MenuItem>
        <MenuItem onClick={onOpenSentimentMenu}>
          <ListItemIcon>{getSentimentIcon(article.sentiment, 22)}</ListItemIcon>
          <ListItemText>Sentimento</ListItemText>
        </MenuItem>
        <MenuItem onClick={onOpenExposureMenu}>
          <ListItemIcon>{getExposureIcon(article.exposure, 22)}</ListItemIcon>
          <ListItemText>Exposição da Marca</ListItemText>
        </MenuItem>
        {kpiaMenuItem}
        {showKpia && <Divider sx={{ my: 0.5 }} />}
        {onEdit && (
          <MenuItem onClick={closeAfter(() => onEdit(article))}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={closeAfter(() => onDelete(article._id))} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

ArticleModalActionMenu.propTypes = {
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  article: PropTypes.shape({
    _id: PropTypes.string,
    exposure: PropTypes.string,
    isFavorite: PropTypes.bool,
    isRead: PropTypes.bool,
    sentiment: PropTypes.string
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
  kpiaAnalyzing: PropTypes.bool,
  onAddToCollection: PropTypes.func.isRequired,
  onAnalyzeKpia: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onOpenExposureMenu: PropTypes.func.isRequired,
  onOpenPublicLink: PropTypes.func.isRequired,
  onOpenSentimentMenu: PropTypes.func.isRequired,
  onOpenShare: PropTypes.func.isRequired,
  onOpenTagDialog: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  qualitativeAnalysisApplied: PropTypes.bool,
  readOnly: PropTypes.bool,
  showKpia: PropTypes.bool
};

ArticleModalActionMenu.defaultProps = {
  anchorEl: null,
  kpiaAnalyzing: false,
  onAnalyzeKpia: () => {},
  onDelete: null,
  onEdit: null,
  qualitativeAnalysisApplied: false,
  readOnly: false,
  showKpia: false
};

export default ArticleModalActionMenu;
